/**
 * 프로젝트 데이터 다운로드 훅
 *
 * 온라인 시 projectMachineGroups 쿼리를 호출하여
 * 프로젝트 하위 모든 설비 데이터를 IndexedDB에 저장합니다.
 */

import { useState, useCallback } from "react";
import { useApolloClient } from "@apollo/client";
import {
  PROJECT_MACHINE_GROUPS_QUERY,
  type ProjectMachineGroupsData,
  type ProjectMachineGroupsVariables,
  type ProjectMachineGroupMachine,
} from "@/lib/graphql/equipment";
import type { MeasureDiscusItem } from "@ict/shared";
import { cacheMachine } from "@/lib/db/machines";
import { useNetworkStatus } from "./useNetworkStatus";
import type { CachedMachine } from "@/lib/db/types";
import type { CheckResultValueNullable } from "@/types";
import { createLogger } from "@ict/shared";

const logger = createLogger({ module: "useProjectDownload" });

export interface ProjectDownloadState {
  /** 다운로드 진행 중 여부 */
  downloading: boolean;
  /** 현재 진행률 (0~100) */
  progress: number;
  /** 전체 설비 수 */
  total: number;
  /** 다운로드된 설비 수 */
  downloaded: number;
  /** 에러 메시지 */
  error: string | null;
  /** 마지막 다운로드 시점 */
  lastDownloadedAt: number | null;
}

export interface UseProjectDownloadResult extends ProjectDownloadState {
  /** 프로젝트 다운로드 실행 */
  downloadProject: (projectId: string) => Promise<boolean>;
  /** 다운로드 상태 초기화 */
  resetDownload: () => void;
}

const initialState: ProjectDownloadState = {
  downloading: false,
  progress: 0,
  total: 0,
  downloaded: 0,
  error: null,
  lastDownloadedAt: null,
};

/**
 * 서버에서 받은 설비 데이터를 CachedMachine 형태로 변환
 */
function convertToCachedMachine(
  projectId: string,
  projectName: string,
  projectType: string,
  docKey: string,
  machine: ProjectMachineGroupMachine,
  measureDiscus?: MeasureDiscusItem[]
): CachedMachine {
  const machineId = machine.id;

  return {
    _id: `${projectId}-${docKey}-${machineId}`,
    projectId,
    // 프로젝트 정보 캐시 (오프라인에서 checkList/maintenance 구분에 필요)
    project: {
      id: projectId,
      name: projectName,
      type: projectType,
      measureDiscus,
    },
    group: {
      _id: docKey,
      docKey,
      docLabel: docKey,
    },
    machine: {
      _id: machineId,
      machineId,
      name: machine.title,
      model: machine.modelNo,
      manufacturer: machine.modelCreator,
    },
    checkList:
      machine.checkList?.map((item) => ({
        _id: item.id,
        category: item.group || "",
        item: item.title,
        result: (item.result || null) as CheckResultValueNullable,
        image: item.image,
        imageO: item.imageO,
        imageX: item.imageX,
        underReason: item.underReason,
        underSolution: item.underSolution,
      })) || [],
    maintenance:
      machine.maintenance?.map((item) => ({
        _id: item.id,
        category: item.group || "",
        item: item.title,
        result: (item.result || null) as CheckResultValueNullable,
        image: item.image,
        imageO: item.imageO,
        imageX: item.imageX,
        underReason: item.underReason,
        underSolution: item.underSolution,
      })) || [],
    dataList:
      machine.data?.map((item) => ({
        _id: item.id,
        category: item.section,
        item: item.title,
        value: item.checkValue,
        unit: item.unit,
      })) || [],
    pictures:
      machine.pictures?.map((url, idx) => ({
        _id: `${machineId}-pic-${idx}`,
        url,
        createdAt: new Date().toISOString(),
      })) || [],
    measures: machine.measures ?? null,
    synced: true,
    syncStatus: "synced",
    cachedAt: Date.now(),
    serverVersion: machine.version,
  };
}

/**
 * 프로젝트 데이터 다운로드 훅
 */
export function useProjectDownload(): UseProjectDownloadResult {
  const client = useApolloClient();
  const { isOffline } = useNetworkStatus();
  const [state, setState] = useState<ProjectDownloadState>(initialState);

  const downloadProject = useCallback(
    async (projectId: string): Promise<boolean> => {
      // 오프라인이면 다운로드 불가
      if (isOffline) {
        setState((prev) => ({
          ...prev,
          error: "오프라인 상태에서는 다운로드할 수 없습니다.",
        }));
        return false;
      }

      // 이미 다운로드 중이면 중복 실행 방지
      if (state.downloading) {
        return false;
      }

      setState({
        downloading: true,
        progress: 0,
        total: 0,
        downloaded: 0,
        error: null,
        lastDownloadedAt: null,
      });

      try {
        // GraphQL 쿼리 실행
        const { data, error } = await client.query<
          ProjectMachineGroupsData,
          ProjectMachineGroupsVariables
        >({
          query: PROJECT_MACHINE_GROUPS_QUERY,
          variables: { projectId },
          fetchPolicy: "network-only",
        });

        if (error) {
          throw error;
        }

        if (!data?.projectMachineGroups) {
          throw new Error("프로젝트 데이터를 가져올 수 없습니다.");
        }

        const { machineGroups, projectName, projectType, measureDiscus } =
          data.projectMachineGroups;

        // 전체 설비 수 계산
        const totalMachines = machineGroups.reduce((sum, group) => sum + group.machines.length, 0);

        if (totalMachines === 0) {
          setState({
            downloading: false,
            progress: 100,
            total: 0,
            downloaded: 0,
            error: null,
            lastDownloadedAt: Date.now(),
          });
          return true;
        }

        setState((prev) => ({
          ...prev,
          total: totalMachines,
        }));

        // 각 설비 그룹의 설비들을 IndexedDB에 저장
        let downloadedCount = 0;

        for (const group of machineGroups) {
          const docKey = group.key;

          for (const machine of group.machines) {
            const cachedMachine = convertToCachedMachine(
              projectId,
              projectName,
              projectType,
              docKey,
              machine,
              measureDiscus
            );

            await cacheMachine(cachedMachine);

            downloadedCount++;
            const progress = Math.round((downloadedCount / totalMachines) * 100);

            setState((prev) => ({
              ...prev,
              downloaded: downloadedCount,
              progress,
            }));
          }
        }

        setState({
          downloading: false,
          progress: 100,
          total: totalMachines,
          downloaded: downloadedCount,
          error: null,
          lastDownloadedAt: Date.now(),
        });

        return true;
      } catch (err) {
        const errorMessage =
          err instanceof Error ? err.message : "다운로드 중 오류가 발생했습니다.";

        setState((prev) => ({
          ...prev,
          downloading: false,
          error: errorMessage,
        }));

        logger.error("다운로드 실패:", err);
        return false;
      }
    },
    [client, isOffline, state.downloading]
  );

  const resetDownload = useCallback(() => {
    setState(initialState);
  }, []);

  return {
    ...state,
    downloadProject,
    resetDownload,
  };
}

export default useProjectDownload;
